/**
 * The customer Settings page's one save bar (docs/features/credit-control/
 * 06_ui_strategy.md §2). Hidden while nothing is edited, so there are no enabled
 * buttons at rest. While something is, it sticks to the bottom of the viewport
 * and reads "Unsaved changes: " followed by the cards that are dirty, with
 * Reset changes and Save. Save opens one reason dialog listing what is
 * changing in plain words; the page then writes to each owning module in turn
 * (see `useSaveSettings`). While an edit is refused inline, Save waits and
 * says why. A failure of the last Save shows here, and the edits stay. It
 * leaves room on the right for the floating Feedback button, which would
 * otherwise cover Save.
 */
import { useState } from "react";
import { Button } from "../../primitives/button";
import { ReasonConfirmDialog } from "../catalogue/ReasonConfirmDialog";

export function SettingsSaveBar({
  customerName,
  dirtyParts,
  summary,
  blockedReason,
  saving,
  message,
  onReset,
  onConfirm,
}: {
  customerName: string;
  /** Names of the cards with unsaved edits: "Payment methods"… */
  dirtyParts: string[];
  /** What is changing, in plain words, one line each. */
  summary: string[];
  /** Why Save is disabled (an edit is refused inline), or null. */
  blockedReason: string | null;
  saving: boolean;
  /** The outcome of the last Save when it did not fully succeed, or null. */
  message: string | null;
  onReset: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  if (dirtyParts.length === 0) return null;
  return (
    <div
      role="region"
      aria-label="Save changes"
      className="sticky bottom-0 z-10 -mx-1 space-y-1 border-t border-border bg-background/95 py-3 pl-1 pr-36 backdrop-blur"
    >
      {message && (
        <p role="alert" className="text-sm text-destructive">
          {message}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm">
          <span className="font-medium">Unsaved changes:</span>{" "}
          {dirtyParts.join(", ")}
          {blockedReason && (
            <span className="block text-xs text-muted-foreground">
              {blockedReason}
            </span>
          )}
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={saving}
            onClick={onReset}
          >
            Reset changes
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={saving || blockedReason !== null}
            onClick={() => setConfirming(true)}
          >
            Save
          </Button>
        </div>
      </div>
      <ReasonConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Save changes to ${customerName}'s settings?`}
        description="From now on, orders and invoices follow the new settings. Orders already placed and invoices already raised are not changed."
        details={
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {summary.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        }
        reasonLabel="Why is it changing?"
        confirmLabel="Save"
        onConfirm={onConfirm}
      />
    </div>
  );
}
